import * as React from "react";
import { Alert, Box, Grid } from "@mui/material";
import {
    AssessmentNotificationStatus,
    SessionDetailType,
    SessionKeyDates,
} from "../../services/types/sessionDetailTypes";
import CclGenericAccordian from "../../components/common/cclGenericAccordian";
import CclInfoTable, {
    CclInfoTableFormValue,
    CclInfoTableRowTemplate,
} from "../../components/common/cclInfoTable/cclInfoTable";
import { cclInfoTblAssessmentStatusSessionInfoTemplate } from "../../pages/sessions/sessionDetail/assessments/scoringStatus/cclInfoTblAssessmentStatusSessionInfoTemplate";
import { CclInfoTblAssessmentStatusSessionDatesTemplate } from "../../pages/sessions/sessionDetail/assessments/scoringStatus/cclInfoTblAssessmentStatusSessionDatesTemplate";
import {
    useGetSessionSetUpByEventKeyQuery,
    useUpdateAssessmentNotificationStatusMutation,
} from "../../services/cclTokenedSessionApi";
import useLogAccessEvent from "../../hooks/useLogAccessEvent";
import CclAlertSnackbar from "../../components/common/cclAlertSnackbar";
import { parseDateAsLocal } from "../../utilities/dateHelpers";

const parseLocalDate = (dt: Date | null | undefined): Date | null => {
    if (dt == null) return null;
    const d = new Date(dt);
    return isNaN(d.getTime()) ? null : parseDateAsLocal(d);
};

interface PlatformDatesAccordionProps {
    sessionDetails: SessionDetailType | undefined;
    readonly?: boolean;
    fromMyAccess?: boolean;
    keyDatesExpanded?: boolean;
    onKeyDatesExpandedChange?: (expanded: boolean) => void;
}

const PlatformDatesAccordion: React.FC<PlatformDatesAccordionProps> = (props) => {
    const { data: sessionSetupData } = useGetSessionSetUpByEventKeyQuery(
        props.sessionDetails?.sessionKey.toString() ?? "",
        {
            skip: props.sessionDetails == null,
        }
    );

    const [updateNotifcationStatus] = useUpdateAssessmentNotificationStatusMutation();

    const { logEvent } = useLogAccessEvent();

    const [prework, setPreWork] = React.useState<boolean>(
        sessionSetupData?.sessionProgramData.preworkInvitationStatus ?? false
    );
    const [reminders, setReminders] = React.useState<boolean>(
        sessionSetupData?.sessionProgramData.reminderStatus ?? false
    );
    const [keyDates, setKeyDates] = React.useState<SessionKeyDates | null>(null);
    const [showMessage, setShowMessage] = React.useState<boolean>(false);
    const [message, setMessage] = React.useState<string>("");
    const [severity, setSeverity] = React.useState<"success" | "info" | "error">("success");

    const [infoTemplate, setInfoTemplate] = React.useState<CclInfoTableRowTemplate[]>([]);
    const [datesTemplate, setDatesTemplate] = React.useState<CclInfoTableRowTemplate[]>([]);

    const handleNotificationChange = (changes: CclInfoTableFormValue[]) => {
        let pw = changes.find((x) => x.name === "preworkSwitch");
        let r = changes.find((x) => x.name === "remindersEnabled");

        let event = "";

        let payload: AssessmentNotificationStatus = {
            eskey: props.sessionDetails?.sessionKey ?? 0,
            payload: {
                preworkInvitationStatus: prework,
                reminderStatus: reminders,
            },
        };

        if (pw) {
            setPreWork(pw.value as boolean);
            payload.payload.preworkInvitationStatus = pw.value as boolean;
            if (pw.value) {
                setMessage("Pre-Work Invitations Turned On");
                event = "PreWorkInvitationsTurnedOn";
            } else {
                setMessage("Pre-Work Invitations Turned Off");
                event = "PreWorkInvitationsTurnedOff";
            }
        }
        if (r) {
            setReminders(r.value as boolean);
            payload.payload.reminderStatus = r.value as boolean;
            if (r.value) {
                setMessage("Reminders Turned On");
                event = "RemindersTurnedOn";
            } else {
                setMessage("Reminders Turned Off");
                event = "RemindersTurnedOff";
            }
        }

        handleNotificationStatusUpdate(payload, event);
    };

    const handleNotificationStatusUpdate = (
        payload: AssessmentNotificationStatus,
        event: string
    ) => {
        updateNotifcationStatus(payload)
            .unwrap()
            .then(() => {
                setShowMessage(true);
                setSeverity("success");

                const eventData = {
                    sessionID: props.sessionDetails?.sessionKey.toString() ?? "",
                    projectCode: props.sessionDetails?.sessionCode ?? "",
                };
                logEvent(event, eventData);
            })
            .catch((error) => {
                console.error("Failed to update notification status: ", error);
            });
    };

    React.useEffect(() => {
        if (sessionSetupData == null) {
            setKeyDates(null);
            return;
        }

        if (!props.sessionDetails) return;

        const spd = sessionSetupData.sessionProgramData;

        // Build a focused dates object — all nullable — converting from UTC strings to local midnight
        setKeyDates({
            preworkInvitationDate: parseLocalDate(spd.preworkInvitationDate),
            surveyCompletionDate: parseLocalDate(spd.surveyCompletionDate),
            reminder1: parseLocalDate(spd.reminder1),
            reminder2: parseLocalDate(spd.reminder2),
            reminder3: parseLocalDate(spd.reminder3),
        });

        let infoTemplateTemp = cclInfoTblAssessmentStatusSessionInfoTemplate;
        let dateTemplateTemp = CclInfoTblAssessmentStatusSessionDatesTemplate;

        if (!props.fromMyAccess) {
            infoTemplateTemp = infoTemplateTemp.filter(
                (x) => x.id !== "hostingPlatform" && x.id !== "assessmentPlatform"
            );
        }

        setInfoTemplate(infoTemplateTemp);
        setDatesTemplate(dateTemplateTemp);

        let rowItem = infoTemplateTemp.find((x) => x.id === "hostingPlatform");
        if (rowItem) {
            rowItem.valueField.value = props.sessionDetails.hostingPlatform ?? "";
        }

        rowItem = infoTemplateTemp.find((x) => x.id === "assessmentPlatform");
        if (rowItem) {
            rowItem.valueField.value = props.sessionDetails.assessmentPlatform ?? "";
        }

        rowItem = infoTemplateTemp.find((x) => x.id === "preworkSwitch");
        if (rowItem) {
            rowItem.valueField.checked = spd.preworkInvitationStatus ?? false;
        }

        rowItem = dateTemplateTemp.find((x) => x.id === "remindersEnabled");
        if (rowItem) {
            rowItem.valueField.checked = spd.reminderStatus ?? false;
        }

        if (props.readonly ?? false) {
            infoTemplateTemp.forEach((item) => {
                if (
                    item.id !== "preworkSwitch" ||
                    (props.fromMyAccess === true && item.id === "preworkSwitch")
                ) {
                    item.editable = false;
                }
            });
            dateTemplateTemp.forEach((item) => {
                if (
                    item.id !== "remindersEnabled" ||
                    (props.fromMyAccess === true && item.id === "remindersEnabled")
                ) {
                    item.editable = false;
                }
            });
        }
    }, [sessionSetupData, props.sessionDetails, props.readonly, props.fromMyAccess]);

    if (props.sessionDetails === undefined) {
        return (
            <Box>
                <Alert severity="error">Error: Session data not found!</Alert>
            </Box>
        );
    }

    return (
        <>
            <CclAlertSnackbar
                open={showMessage}
                message={message}
                severity={severity}
                onClose={() => setShowMessage(false)}
            />
            <CclGenericAccordian
                headingText={props.fromMyAccess ? "Platforms & Key Dates" : "Key Dates"}
                defaultExpanded={true}
                noGutter={true}
                expanded={props.keyDatesExpanded}
                onExpandedChange={props.onKeyDatesExpandedChange}
            >
                <Grid container spacing={2}>
                    <Grid item xs={6}>
                        <CclInfoTable
                            data={keyDates ?? {}}
                            template={infoTemplate}
                            hideSave
                            onChange={handleNotificationChange}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <CclInfoTable
                            data={keyDates ?? {}}
                            template={datesTemplate}
                            onChange={handleNotificationChange}
                            hideSave
                        />
                    </Grid>
                </Grid>
            </CclGenericAccordian>
        </>
    );
};

export default PlatformDatesAccordion;
